import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import Unicon from '@/components/General/Unicon';
import DropdownMenu from '@/components/General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';
import FileIcon from './FileIcon';
import { formatSize } from '@/utils/FileManager';
import { parseTimeFrame } from '@/utils/dater';

const FileRow = ({ file, onPress, onLongPress, isSelected, onSelect, viewMode = 'list', onRename, onMove, onDelete, onShare }) => {

  const menu = (
    <DropdownMenu
      triggerComponent={
        <View testID={`file-menu-${file.id}`} style={styles.menuTrigger}>
          <Unicon name="ellipsis-vertical" size={20} colorVariant="textSecondary" />
        </View>
      }
      options={[
        { name: 'Open', icon: { web: 'open-outline', ios: 'arrow.up.right.square', android: 'open_in_new' }, onSelect: () => onPress?.(file) },
        { name: 'Share', icon: { web: 'share-social-outline', ios: 'square.and.arrow.up', android: 'share' }, onSelect: () => onShare?.(file) },
        { name: 'Rename', icon: { web: 'pencil', ios: 'pencil', android: 'edit' }, onSelect: () => onRename?.(file) },
        { name: 'Move', icon: { web: 'folder-outline', ios: 'folder', android: 'drive_file_move' }, onSelect: () => onMove?.(file) },
        { name: 'Delete', icon: { web: 'trash', ios: 'trash', android: 'delete' }, destructive: true, onSelect: () => onDelete?.(file) },
      ]}
    />
  );

  if (viewMode === 'grid') {
    return (
      <TouchableOpacity
        testID={`file-row-${file.id}`}
        onPress={() => onPress?.(file)}
        onLongPress={() => onLongPress?.(file)}
        style={[styles.gridCard, isSelected && styles.gridCardSelected]}
      >
        <View style={styles.gridMenuWrap}>{menu}</View>
        <View style={styles.gridIconWrap}>
          <FileIcon filename={file.filename} mimeType={file.mimeType} size={24} color="textSecondary" />
        </View>
        <Text style={styles.gridName} numberOfLines={2}>{file.filename}</Text>
        <Text style={styles.gridMeta}>{formatSize(file.size || 0)}</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View
      testID={`file-row-${file.id}`}
      style={[styles.row, isSelected && styles.rowSelected]}
    >
      <TouchableOpacity testID={`file-checkbox-${file.id}`} onPress={() => onSelect?.(file)} style={styles.checkbox}>
        <Unicon
          name={isSelected ? 'checkbox-marked' : 'checkbox-blank-outline'}
          size={24}
          colorVariant={isSelected ? 'primary' : 'textSecondary'}
          library="material"
        />
      </TouchableOpacity>

      <TouchableOpacity testID={`file-pressable-${file.id}`} style={styles.rowPressable} onPress={() => onPress?.(file)} onLongPress={() => onLongPress?.(file)}>
        <FileIcon filename={file.filename} mimeType={file.mimeType} size={24} color="textSecondary" />
    
        <View style={styles.nameWrap}>
          <Text style={styles.name} numberOfLines={1}>{file.filename}</Text>
          <Text style={styles.meta}>{formatSize(file.size || 0)} • {parseTimeFrame(file.createdAt)}</Text>
        </View>
      </TouchableOpacity>

      <View style={styles.menuWrap}>
        {menu}
      </View>
    </View>
  );
};

const styles = StyleSheet.create(({ colors }) => ({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rowPressable: {
    flex:1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuWrap: {
    alignSelf: 'flex-end',
  },
  rowSelected: {
    backgroundColor: colors.primary + '20',
  },
  checkbox: {
    marginRight: 12,
  },
  nameWrap: {
    flex: 1,
    marginLeft: 12,
  },
  name: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text 
  },
  meta: {
    fontSize: 10,
    fontWeight: '400',
    color: colors.textSecondary,
  },
  menuTrigger: {
    padding: 4,
    borderRadius: 6,
    _web: { cursor: 'pointer', _hover: { opacity: 0.7 } },
  },
  gridCard: {
    flex: 1,
    margin: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    padding: 12,
    alignItems: 'center',
    gap: 6,
    minWidth: 100,
    maxWidth: 200,
  },
  gridCardSelected: {
    backgroundColor: colors.primary + '20',
  },
  gridMenuWrap: {
    position: 'absolute',
    top: 6,
    right: 6,
  },
  gridIconWrap: {
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridName: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
    color: colors.text,
  },
  gridMeta: {
    fontSize: 11,
    color: colors.textSecondary,
  },
}));

export default FileRow;
